// Workspace member + invitation RPC facades (knowledge/87 Phase 1).
// Extracted verbatim from app.svelte.ts — these are pure async client.* facades
// (plus reactive mutations of the shared workspaceState/authState proxies, which
// stay reactive from a plain .ts). No $state/$derived/$effect runes live here, so
// this is a plain .ts, NOT .svelte.ts. app.svelte.ts re-exports every symbol below
// so importers (settings/members, invite/[token], onboarding) never change.
import { client } from "./client.js";
import type { Handoff } from "../components/agents/handoff/types.js";
import { workspaceState, authState } from "../core/state.svelte.js";
// License/upgrade gate handlers live in the shared leaf module (also used by
// app.svelte.ts + billing) — imported DOWN here, not cycled through the barrel.
import { handleLicenseError, handleUpgradeError } from "./license-errors.js";
import {
  fetchInvitePreview,
  requestInviteRenewal,
  type PublicInvitePreview,
} from "../core/client.js";
import { mergeMembershipRefresh } from "../core/workspace-list.js";

// A still-pending invitation row for the Members settings "Pending Invitations"
// section. Mirrors the relay's cyborg:list_pending_invitations payload.
export interface PendingInvitation {
  id: string;
  email: string;
  role: string;
  createdAt: number;
  expiresAt: number;
  createdByName?: string;
}

export async function inviteMember(
  email: string,
  role: "admin" | "member" | "viewer" = "member",
  channelIds?: string[],
  // Additive: see client.inviteMember. The channel Members tab passes keepChannelIds so a
  // re-invite adds this channel to a pending invite rather than replacing its channels.
  opts?: { keepChannelIds?: boolean },
): Promise<{ invitationId: string; inviteUrl: string; alreadyInvited: boolean; role?: string }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  let invite: Awaited<ReturnType<typeof client.inviteMember>>;
  try {
    invite = await client.inviteMember(workspaceState.current.id, email, role, channelIds, opts);
  } catch (err) {
    if (!handleLicenseError(err)) handleUpgradeError(err);
    throw err;
  }
  const { invitationId, inviteUrl, alreadyInvited } = invite;
  const { members, external } = await client.listMembers(workspaceState.current.id);
  workspaceState.members = members;
  authState.setMemberImagesFromMembers([...members, ...external]);
  // The role the pending invite now carries (the relay's word, not the one asked for).
  return { invitationId, inviteUrl, alreadyInvited, role: invite.membership?.role };
}

// Reusable workspace invite link. Both wrappers hit NEW relay endpoints that
// don't exist on prod yet — callers must try/catch and degrade gracefully.
export async function getOpenInvite(): Promise<{
  token: string;
  inviteUrl: string;
  role: string;
  channelIds: string[];
} | null> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.getOpenInvite(workspaceState.current.id);
}

export async function upsertOpenInvite(
  role: string,
  channelIds: string[],
  rotate = false,
): Promise<{ token: string; inviteUrl: string; role: string; channelIds: string[] }> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  return client.upsertOpenInvite(workspaceState.current.id, role, channelIds, rotate);
}

export async function removeMember(userId: string, handoffs?: Handoff[]): Promise<string[]> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  const failedCyboIds = await client.removeMember(workspaceState.current.id, userId, handoffs);
  workspaceState.members = workspaceState.members.filter((m) => m.userId !== userId);
  return failedCyboIds;
}

export async function updateMemberRole(
  userId: string,
  role: "admin" | "member" | "viewer",
): Promise<void> {
  if (!workspaceState.current) throw new Error("No workspace selected");
  await client.updateRole(workspaceState.current.id, userId, role);
  workspaceState.members = workspaceState.members.map((m) =>
    m.userId === userId ? { ...m, role } : m,
  );
}

// ─── Invitations ──────────────────────────────────────────────────

// Admin/owner: load the workspace's still-pending invitations for the Members
// settings "Pending Invitations" section. Called on mount + after any
// invite/resend/cancel so the list (and each row's expiry countdown) stays
// fresh.
export async function listPendingInvitations(workspaceId?: string): Promise<PendingInvitation[]> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  const { invitations } = await client.listPendingInvitations(wsId);
  return invitations;
}

// Re-issue a pending invitation: resets its 7-day expiry and re-sends the email.
export async function resendInvitation(
  invitationId: string,
  workspaceId?: string,
): Promise<{ invitationId: string; sentAt: number }> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  return client.resendInvitation(wsId, invitationId);
}

// Revoke a pending invitation so its token can no longer be redeemed.
export async function cancelInvitation(
  invitationId: string,
  workspaceId?: string,
): Promise<{ invitationId: string }> {
  const wsId = workspaceId ?? workspaceState.current?.id;
  if (!wsId) throw new Error("No workspace selected");
  return client.cancelInvitation(wsId, invitationId);
}

// Public (unauthenticated) invitation preview for the /invite/:token landing
// page. Re-exported here so routes import everything invitation-related from
// the app state module (the single client surface).
export async function getInvitePreview(wsUrl: string, token: string): Promise<PublicInvitePreview> {
  return fetchInvitePreview(wsUrl, token);
}

// "Ask for a new invite" (public, rate-limited; always ok — it never says what happened).
export async function askForNewInvite(wsUrl: string, token: string): Promise<void> {
  return requestInviteRenewal(wsUrl, token);
}

// Redeem an invitation token for the signed-in user. On success the user is now
// a member of `workspaceId`; refresh the local workspace list (the accept didn't
// re-run cyborg:auth) so the post-accept navigation into /workspace/:id finds it.
// Returns the joined workspaceId so the caller can navigate there, and whether the account
// was already a member (nothing joined: no join wizard to run).
export async function acceptInvitation(
  invitationToken: string,
): Promise<{ workspaceId: string; alreadyMember: boolean }> {
  const { workspaceId, alreadyMember } = await client.acceptInvitation(invitationToken);
  // Subscribe this socket to the freshly-joined workspace's live broadcasts —
  // it wasn't part of the socket's auth-time subscription set.
  // intentional: best-effort live-broadcast subscribe on join; the awaited workspace-list refresh below gates navigation.
  client.subscribeWorkspace(workspaceId).catch(() => {});
  // Refresh the workspace list from server truth so the new membership appears
  // in workspaceState.list (selectWorkspace + the /workspace/:id layout look it
  // up there). Best-effort — the layout falls back to /workspace if absent.
  try {
    const workspaces = await client.fetchWorkspaces();
    // fetch_workspaces answers memberships only — merge, never assign, or joining a
    // workspace evicts the host workspaces this viewer reaches as an external.
    workspaceState.list = mergeMembershipRefresh(workspaceState.list, workspaces);
  } catch {
    // intentional: an older relay or a transient failure; the minimal entry below keeps navigation working.
  }
  // Absent after a failed fetch, or a second tab told alreadyAccepted before the first activated.
  if (!workspaceState.list.some((w) => w.id === workspaceId)) {
    workspaceState.list = [
      ...workspaceState.list,
      {
        id: workspaceId,
        name: "Workspace",
        ownerId: "",
        avatarUrl: null,
        role: "member",
        createdAt: Date.now(),
        settings: {},
      },
    ];
  }
  return { workspaceId, alreadyMember: alreadyMember === true };
}
